import React from 'react';
import { Tooltip } from 'design-system';
import { Spinner, Typography } from '@scale/scaleui';
import { useObjectPreview } from 'corp/hooks/smartId/useObjectPreview';
import { INamedObjectTypes, ITaskAttemptPreviewJson } from '@scale/shared/types/lookup';
import IsomorphicLink from 'corp/components/UI/IsomorphicLink';
import moment from 'moment';
import { ReviewLevel, TaskAttemptReviewStatus } from '@scale/shared/defaults';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

const colorOfTaskStatus: Record<TaskAttemptReviewStatus, string> = {
  [TaskAttemptReviewStatus.Accepted]: 'var(--color-RemoGreen)',
  [TaskAttemptReviewStatus.Fixed]: 'var(--color-RemoGold)',
  [TaskAttemptReviewStatus.BenchmarkEvaluation]: 'var(--color-RemoGreen)',
  [TaskAttemptReviewStatus.BenchmarkGroundTruth]: 'var(--color-RemoGreen)',
  [TaskAttemptReviewStatus.Blacklisted]: 'black',
  [TaskAttemptReviewStatus.Canceled]: 'black',
  [TaskAttemptReviewStatus.CaptchaPending]: 'black',
  [TaskAttemptReviewStatus.Ignored]: '#888',
  [TaskAttemptReviewStatus.Pending]: 'var(--color-ScaleBlue)',
  [TaskAttemptReviewStatus.Rejected]: 'var(--color-RemoRed)',
  [TaskAttemptReviewStatus.Corrupted]: 'var(--color-RemoRed)',
  [TaskAttemptReviewStatus.Processing]: 'var(--color-ScaleBlue)',
  [TaskAttemptReviewStatus.ReviewNotNeeded]: 'black',
};

const TaskAttemptPreview: React.FC<{ id: string }> = ({ id }) => {
  const remoAdminCtx = useRemoAdminCtx();
  const lookupPrefix = remoAdminCtx.isRemoAdmin ? `/remoadmin/tools` : '';
  const {
    value: taskAttempt,
    loading,
    error,
  } = useObjectPreview<ITaskAttemptPreviewJson>(id, INamedObjectTypes.TaskAttempt);
  if (error) return <Typography.Paragraph1>No attempt found</Typography.Paragraph1>;
  if (loading) return <Spinner size="md" className="m-4" />;
  const reviewLevelDisplay = (reviewLevel: ReviewLevel) =>
    reviewLevel === -1 ? 'Attempt' : 'L' + reviewLevel;

  return (
    <div className="taskattempt-preview">
      <style jsx>{`
        .taskattempt-preview__banner {
          font-weight: bold;
          padding: 10px;
          display: flex;
          align-items: center;
        }

        .taskattempt-preview__thing-list > div {
          display: flex;
        }

        .taskattempt-preview__thing-list strong {
          color: #666;
          margin-right: 10px;
          min-width: 120px;
          text-align: right;
        }
      `}</style>
      {taskAttempt && (
        <>
          <div className="taskattempt-preview__banner">
            <span
              style={{
                fontSize: 'smaller',
                backgroundColor: colorOfTaskStatus[taskAttempt.reviewStatus],
                color: 'white',
                borderRadius: '14px',
                height: '28px',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 10px',
              }}
            >
              {taskAttempt.reviewStatus}
            </span>
            <h3 style={{ margin: '0 5px' }}>{id}</h3>
          </div>
          <div className="taskattempt-preview__body">
            <div className="taskattempt-preview__thing-list">
              <div>
                <strong>Lookup</strong>
                <IsomorphicLink
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${lookupPrefix}/lookup/${id}`}
                >
                  {id}{' '}
                </IsomorphicLink>
              </div>
              {
                <div>
                  <strong>Subtask</strong>
                  <IsomorphicLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${lookupPrefix}/lookup/${taskAttempt.subtask}`}
                  >
                    {taskAttempt.subtask}
                  </IsomorphicLink>
                </div>
              }
              {
                <div>
                  <strong>Attempted At Level</strong>
                  <span>{reviewLevelDisplay(taskAttempt.attemptedAtReviewLevel)}</span>
                </div>
              }
              {!!taskAttempt.attemptedBy && (
                <div>
                  <strong>Attempted By</strong>
                  <IsomorphicLink
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${lookupPrefix}/lookup/${taskAttempt.attemptedBy._id}`}
                  >
                    {taskAttempt.attemptedBy.email}
                  </IsomorphicLink>
                </div>
              )}
              {!!taskAttempt?.attemptedAt && (
                <div>
                  <strong>Attempted At</strong>
                  <Tooltip
                    placement={'top'}
                    title={new Date(taskAttempt.attemptedAt).toLocaleString()}
                  >
                    <span>{moment(taskAttempt.attemptedAt).fromNow()}</span>
                  </Tooltip>
                </div>
              )}
              {
                <div>
                  <strong>Reviews ({taskAttempt.reviews.length})</strong>
                  <span>
                    {taskAttempt.reviews.map(r => (
                      <>
                        <IsomorphicLink
                          target="_blank"
                          key={r}
                          rel="noopener noreferrer"
                          href={`${lookupPrefix}/lookup/${r}`}
                        >
                          {r}
                        </IsomorphicLink>
                        ,{' '}
                      </>
                    ))}
                  </span>
                </div>
              }
              {typeof taskAttempt.payoutAmountHundredthsOfCents !== 'undefined' && (
                <div>
                  <strong>Payout</strong>
                  <span>${(taskAttempt.payoutAmountHundredthsOfCents / 10000).toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TaskAttemptPreview;
